'use client';
import React from 'react';
import { Container, Section } from '@layout';
import { SlideOver, Button } from '@ui';
import { withOptionalId } from '@lib/withOptionalId';
import { wideDropdownGroupsRaw } from '@data/links';
import { t as translate } from '@utils/translate';

interface DesignSystemNavProps {
  sectionID?: string;
  classes?: string;
  isSpanish?: boolean;
  /**
   * Optional content rendered to the **left** of the "Open navigation" button, inside the second
   * **`.design-system-nav-content`** column. Host-only escape hatch for app-specific actions
   * (e.g. wiring a **`SearchToggle`** to **`searchSuggestBus.open()`** on apps that don't yet
   * mount **`SearchToggle`** in **`Header.ctaSlot`**). Synced consumers can ignore it — the
   * canonical opener for **`SearchSuggest`** / **`CommandPalette`** remains **`Header.ctaSlot`**.
   */
  ctaSlot?: React.ReactNode;
}

const DesignSystemNav: React.FC<DesignSystemNavProps> = ({
  sectionID,
  classes,
  isSpanish = false,
  ctaSlot,
}) => {
  const [slideOpen, setSlideOpen] = React.useState(false);
  const locale = isSpanish ? 'es' : 'en';

  const pickString = (value: unknown, fallback: string): string =>
    typeof value === 'string' ? value : fallback;

  const heading = pickString(
    translate('ui_kit_nav.heading', 'design-system', locale),
    'Design system — page navigation'
  );
  const openLabel = pickString(
    translate('ui_kit_nav.open_button', 'design-system', locale),
    'Open navigation'
  );
  const title = pickString(
    translate('ui_kit_nav.slide_title', 'design-system', locale),
    'Design system navigation'
  );
  const closeLabel = pickString(translate('ui_kit_nav.close_button', 'design-system', locale), 'Close');
  const itemsRaw = translate('ui_kit_nav.items', 'design-system', locale) as unknown;
  const localizedItems =
    itemsRaw && typeof itemsRaw === 'object'
      ? (itemsRaw as Record<string, string>)
      : ({} as Record<string, string>);

  const groupLabel = (groupId: string) => {
    const key = `ui_kit_nav.groups.${groupId}`;
    const raw = translate(key, 'design-system', locale);
    if (typeof raw === 'string' && raw !== key) return raw;
    return groupId.charAt(0).toUpperCase() + groupId.slice(1);
  };

  return (
    <Section {...withOptionalId(sectionID)} className={classes}>
      <Container>
        <div className='design-system-nav-content'>
          <div>
            <p>
              <b>{heading}</b>
            </p>
          </div>
          <div>
            {ctaSlot}
            <Button
              label={openLabel}
              className='btn-secondary'
              onClick={() => setSlideOpen(true)}
            />
          </div>
        </div>
        <SlideOver
          open={slideOpen}
          onClose={() => setSlideOpen(false)}
          title={title}
          id='modal-slideover'
          className='design-system-nav-slideover'
          actions={
            <Button label={closeLabel} className='btn-ghost' onClick={() => setSlideOpen(false)} />
          }
        >
          <ul className='design-system-nav__groups'>
            {wideDropdownGroupsRaw.map((group) => (
              <li key={group.id} className='design-system-nav__group'>
                <div className='design-system-nav__group-label' id={`design-system-nav-group-${group.id}`}>
                  {groupLabel(group.id)}
                </div>
                <ul
                  className='design-system-nav__group-items'
                  aria-labelledby={`design-system-nav-group-${group.id}`}
                >
                  {group.items.map((item) => (
                    <li key={item.href}>
                      <Button
                        label={localizedItems[item.href] ?? item.text}
                        as='a'
                        href={item.href}
                        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                          e.preventDefault();
                          const href = item.href;
                          setSlideOpen(false);
                          // Overlay body-lock restores pre-open scrollY on close; jump after that.
                          window.setTimeout(() => {
                            const anchor = document.querySelector(href);
                            if (anchor) {
                              anchor.scrollIntoView({ behavior: 'smooth' });
                            }
                            if (href.startsWith('#')) {
                              history.replaceState(null, '', href);
                            }
                          }, 0);
                        }}
                      />
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </SlideOver>
      </Container>
    </Section>
  );
};

export default DesignSystemNav;
